<script async setup>
import ContentProfileSelector from './ContentProfileSelector.vue'
import { getProfiles } from './api'
import { ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useUserStore } from './../authentication/user.store'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'
import GbDialog from '@/components/GbDialog.vue'
import GbButton from '@/components/GbButton.vue'

const fetchingProfileChoices = ref(false)
const profileChoices = ref([])
const confirmDisabled = ref(true)
const selectedProfile = ref()

const contentProfileStore = useContentProfileStore()
const visibilityStore = useVisibilityStore()

const { userId } = storeToRefs(useUserStore())
const { profileId, fetchingActiveProfile, updatingAccountProfile, isProfileSelectorOpen } =
  storeToRefs(contentProfileStore)
const { canChangeProfile } = storeToRefs(visibilityStore)
const { setActiveProfileById, updateAccountProfileId } = contentProfileStore
await fetchProfileChoices()

async function fetchProfileChoices() {
  fetchingProfileChoices.value = true
  try {
    let profiles = await getProfiles()
    profileChoices.value = profiles
  } catch (e) {
    console.error(e)
    profileChoices.value = []
  }
  fetchingProfileChoices.value = false
}

async function onConfirm() {
  if (!selectedProfile.value) {
    closeModal()
    return
  }
  await Promise.all([
    setActiveProfileById(selectedProfile.value),
    updateAccountProfileId({ userUuid: userId.value, profileId: selectedProfile.value })
  ])
  closeModal()
}

function closeModal() {
  isProfileSelectorOpen.value = false
}
</script>
<template>
  <GbDialog
    dialog-id="content-profile-selector"
    :visible="isProfileSelectorOpen"
    @update:visible="(visible) => !visible && closeModal()"
    :header="canChangeProfile ?  'Select Your State' : 'Select Content Profile'"
    :footerButtons="true"
  >
    <template #bodyContent>
      <div id="profile-selector-description" class="sr-only">Dialog to select a content profile for your account</div>
      <div class="overflow-visible">
        <ContentProfileSelector
          v-if="!fetchingProfileChoices && !fetchingActiveProfile && !updatingAccountProfile"
          :profile-choices="profileChoices"
          :currently-active-profile-id="profileId"
          @contentprofile:confirmEnabled="
            ($event) => {
              confirmDisabled = !$event
            }
          "
          @contentprofile:changed="
            ($event) => {
              selectedProfile = $event
            }
          "
        />
        <p
          class="loading-active-profile text-weak"
          v-if="fetchingActiveProfile || updatingAccountProfile"
        >
          <i class="fa fa-circle-o-notch fa-spin" /> Setting profile...
        </p>
        <p class="loading-profiles text-weak" v-if="fetchingProfileChoices">
          <i class="fa fa-circle-o-notch fa-spin" /> Loading profiles...
        </p>
      </div>
    </template>
    
    <template #footerButtonLeft>
      <GbButton 
        label="Cancel" 
        gbTheme="grayOutlineSecondary" 
        @click="closeModal" 
      />
    </template>
    
    <template #footerButtonRight>
      <GbButton 
        label="Confirm" 
        gbTheme="greenPrimary" 
        @click="onConfirm" 
        :disabled="confirmDisabled" 
      />
    </template>
  </GbDialog>
</template>
<style>
</style>
